// frontend/src/components/TreatmentPlanTab.js

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";
import { SearchIcon } from "lucide-react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import ReactDOM from "react-dom";
import { computeFixedDropdownPosition, fixedDropdownStyle } from "./dropdownPositionUtils";
import SaveTreatmentPlanModal from "./SaveTreatmentPlanModal";
import TreatmentPlanPickerModal from "./TreatmentPlanPickerModal";

// ===================== HELPER FUNCTIONS =====================

const makeRowId = (seed) =>
  `${seed}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const extractId = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.$oid) return value.$oid;
  if (typeof value === "object" && value._id) return extractId(value._id);
  if (typeof value === "object" && value.toString && value.toString() !== "[object Object]") {
    return value.toString();
  }
  if (typeof value === "object" && value.id) return extractId(value.id);
  return null;
};

const getProcedurePrice = (procedure) => {
  return procedure.fixed_price || 0;
};

const mapProcedureProducts = (procDoc) => {
  if (Array.isArray(procDoc?.products) && procDoc.products.length > 0) {
    return procDoc.products.map((item) => {
      const prod = item.product_id && typeof item.product_id === "object" ? item.product_id : null;
      const id = extractId(item.product_id);
      return {
        _id: id,
        name: prod?.name || "Unknown Product",
        sku_number: prod?.sku_number || "",
        unit: prod?.unit || item.unit || "",
        usage_qty: item.usage_qty || item.amount_used || 1,
        usage_unit: item.usage_unit || "",
      };
    });
  }
  return (procDoc?.product_ids || []).map((prod) => ({
    _id: extractId(prod) || prod,
    name: prod?.name || "Unknown Product",
    sku_number: prod?.sku_number || "",
    unit: prod?.unit || "",
    usage_qty: 1,
    usage_unit: "",
  }));
};

const USAGE_UNIT_OPTIONS = [
  "ml", "l", "gm", "kg", "mg", "vial", "tablet", "capsule",
  "pcs", "bottle", "ampoule", "syringe", "dose", "drop", "spray", "tube", "pack",
];

// ===================== UTILITY COMPONENTS =====================

const FieldLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
    {children}
  </p>
);

// ===================== SEARCHABLE PROCEDURE SELECT =====================

const SearchableProcedureSelect = ({ excludeIds = [], onSelect, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const response = await httpService.get(
          `/vendorvetclinicservice/get-service-procedures?status=active`
        );
        if (response.code === 200) {
          const term = searchTerm.toLowerCase();
          const filtered = (response.data || []).filter(
            (proc) =>
              !excludeIds.includes(extractId(proc._id)) &&
              (proc.name?.toLowerCase().includes(term) ||
                proc.client_friendly_name?.toLowerCase().includes(term) ||
                proc.barcode?.toLowerCase().includes(term))
          );
          setResults(filtered);
        }
      } catch (err) {
        console.error("Failed to search procedures:", err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSelect = (proc) => {
    onSelect(proc);
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || "Search service procedures..."}
          className="w-full pl-8 pr-3 py-2 text-[12px] border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
        />
      </div>

      {isOpen && searchTerm.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-[11px] text-slate-400 text-center">Searching...</div>
          ) : results.length > 0 ? (
            results.map((proc) => {
              const price = getProcedurePrice(proc);
              return (
                <button
                  key={proc._id}
                  type="button"
                  onClick={() => handleSelect(proc)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-emerald-50 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-[12px] font-medium text-slate-800 truncate">{proc.name}</div>
                    <div className="text-[10px] text-slate-400">₹{price}</div>
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-600 shrink-0 ml-2">Add</span>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-[11px] text-slate-400 text-center">No procedures found</div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== PROCEDURES SECTION =====================

const ProceduresSection = ({ procedures = [], onChange }) => {
  const [productData, setProductData] = useState({});
  const [loadingProducts, setLoadingProducts] = useState({});
  const proceduresRef = useRef(procedures);

  useEffect(() => { proceduresRef.current = procedures; }, [procedures]);

  const fetchProcedureProducts = useCallback(async (procedureId, rowId) => {
    setLoadingProducts(prev => ({ ...prev, [rowId]: true }));
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-service-procedure/${procedureId}`
      );
      if (response.code !== 200) throw new Error("Failed to load procedure");
      const products = mapProcedureProducts(response.data);
      setProductData(prev => ({ ...prev, [rowId]: products }));
    } catch (err) {
      console.error(err);
      setProductData(prev => ({ ...prev, [rowId]: [] }));
    } finally {
      setLoadingProducts(prev => ({ ...prev, [rowId]: false }));
    }
  }, []);

  const handleAddProcedure = useCallback((procedure) => {
    const rowId = makeRowId(procedure._id);
    const price = getProcedurePrice(procedure);
    const newRow = {
      row_id: rowId,
      procedure_id: procedure._id,
      procedure_name: procedure.name,
      procedure_description: procedure.description || "",
      price: price,
      occurrences: 1,
    };

    const currentProcedures = proceduresRef.current;
    onChange([...currentProcedures, newRow]);
    fetchProcedureProducts(procedure._id, rowId);
  }, [onChange, fetchProcedureProducts]);

  const handleRemove = useCallback((rowId) => {
    const currentProcedures = proceduresRef.current;
    onChange(currentProcedures.filter((p) => p.row_id !== rowId));
    setProductData(prev => { const newState = {...prev}; delete newState[rowId]; return newState; });
    setLoadingProducts(prev => { const newState = {...prev}; delete newState[rowId]; return newState; });
  }, [onChange]);

  const handleOccurrencesChange = useCallback((rowId, occurrences) => {
    const currentProcedures = proceduresRef.current;
    onChange(currentProcedures.map((p) => (p.row_id === rowId ? { ...p, occurrences } : p)));
  }, [onChange]);

  const existingIds = procedures.map((p) => p.procedure_id);
  const subtotal = procedures.reduce((sum, p) => sum + (p.price || 0) * (p.occurrences || 1), 0);

  // Fetch products for existing procedures on mount
  useEffect(() => {
    procedures.forEach((proc) => {
      if (proc.procedure_id && !productData[proc.row_id] && !loadingProducts[proc.row_id]) {
        fetchProcedureProducts(proc.procedure_id, proc.row_id);
      }
    });
  }, [procedures]);

  return (
    <div>
      <FieldLabel>Services / Procedures</FieldLabel>
      <SearchableProcedureSelect excludeIds={existingIds} onSelect={handleAddProcedure} />

      {procedures.length === 0 ? (
        <p className="mt-3 text-[11px] text-slate-400">No procedures added yet — search above to add one.</p>
      ) : (
        <div className="mt-3 space-y-3">
          {procedures.map((proc, idx) => {
            const products = productData[proc.row_id] || [];
            const isLoading = loadingProducts[proc.row_id];
            const hasProducts = products.length > 0;

            return (
              <div key={proc.row_id} className="border border-slate-200 rounded-lg overflow-hidden">
                {/* Procedure header */}
                <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-slate-50">
                  <div className="col-span-5 flex items-center gap-2">
                    <span className="text-[12px] font-medium text-slate-800">
                      #{idx + 1} {proc.procedure_name}
                    </span>
                  </div>
                  <div className="col-span-3 flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleOccurrencesChange(proc.row_id, Math.max(1, (proc.occurrences || 1) - 1))}
                      disabled={proc.occurrences <= 1}
                      className="h-6 w-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <MinusIcon className="h-3 w-3" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={proc.occurrences || 1}
                      onChange={(e) => handleOccurrencesChange(proc.row_id, Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-12 text-center border border-slate-200 rounded-md py-1 text-[12px]"
                    />
                    <button
                      type="button"
                      onClick={() => handleOccurrencesChange(proc.row_id, (proc.occurrences || 1) + 1)}
                      className="h-6 w-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                    >
                      <PlusIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <div className="col-span-3 text-[11px] text-slate-500 text-right">
                    ₹{(proc.price || 0) * (proc.occurrences || 1)}
                  </div>
                  <div className="col-span-1 text-right">
                    <button 
                      type="button" 
                      onClick={() => handleRemove(proc.row_id)} 
                      className="p-1 text-red-400 hover:text-red-600 transition-colors"
                      aria-label="Remove procedure"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Products section - only show if there are products */}
                {hasProducts && (
                  <div className="px-3 py-2 bg-white border-t border-slate-100">
                    {isLoading ? (
                      <div className="text-[11px] text-slate-400 text-center py-2">
                        Loading products...
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                            {products.length} item{products.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {products.map((prod) => (
                            <div
                              key={prod._id}
                              className="flex items-center justify-between py-1 px-2 rounded hover:bg-slate-50"
                            >
                              <div className="flex items-center gap-2 flex-1">
                                <span className="text-[11px] font-medium text-slate-700">
                                  {prod.name}
                                </span>
                                <span className="text-[10px] text-slate-400">—</span>
                                <span className="text-[10px] font-medium text-slate-500">
                                  {prod.usage_qty || 1} {prod.usage_unit || "unit"} (Usage)
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                {prod.sku_number && (
                                  <span className="text-slate-400">SKU: {prod.sku_number}</span>
                                )}
                                {prod.sku_number && prod.unit && <span>|</span>}
                                {prod.unit && (
                                  <span className="text-slate-400">Unit: {prod.unit}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* <div className="flex justify-end items-center gap-3 pt-2 border-t border-slate-200 mt-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase">Procedures Subtotal:</span>
            <span className="text-[13px] font-bold text-emerald-600">₹{subtotal.toFixed(2)}</span>
          </div> */}
        </div>
      )}
    </div>
  );
};

// ===================== EXTRA PRODUCTS SECTION =====================

const ExtraProductsSection = ({ products = [], onChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, flipUp: false });
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const reposition = () => updateDropdownPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [showDropdown]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        setShowDropdown(false);
        return;
      }
      setIsSearching(true);
      try {
        const response = await httpService.get(
          `/vendorproduct/searchvendorproducts?search=${encodeURIComponent(searchTerm)}`
        );
        if (response.code === 200) {
          const selectedIds = new Set(products.map((p) => p._id));
          const filteredResults = (response.data || []).filter((p) => !selectedIds.has(p._id));
          setResults(filteredResults);
          if (filteredResults.length > 0) {
            updateDropdownPosition();
            setShowDropdown(true);
          }
        }
      } catch (err) {
        console.error("Failed to search products:", err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, products]);

  const updateDropdownPosition = () => {
    if (inputRef.current) {
      setDropdownPosition(computeFixedDropdownPosition(inputRef.current, { maxHeight: 256, gap: 4 }));
    }
  };

  const handleAdd = (product) => {
    onChange([
      ...products,
      {
        _id: product._id,
        name: product.name,
        sku_number: product.sku_number || "",
        unit: product.unit || "",
        price: product.effective_sale_price || product.sale_price || product.mrp || 0,
        quantity: 1,
        usage_qty: 1,
        usage_unit: "",
        source: "treatment_plan",
        isFromPlan: true,
      },
    ]);
    setSearchTerm("");
    setResults([]);
    setShowDropdown(false);
    if (inputRef.current) inputRef.current.focus();
  };

  const handleRemove = (productId) => {
    onChange(products.filter((p) => p._id !== productId));
  };

  const handleQuantity = (productId, delta) => {
    onChange(products.map((p) => (p._id === productId ? { ...p, quantity: Math.max(1, (p.quantity || 1) + delta) } : p)));
  };

  const handleField = (productId, field, value) => {
    onChange(products.map((p) => (p._id === productId ? { ...p, [field]: value } : p)));
  };

  const handleUsageQtyInput = (productId, value) => {
    if (value === "") {
      handleField(productId, "usage_qty", "");
      return;
    }
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed >= 0) {
      handleField(productId, "usage_qty", Math.max(0.1, parsed));
    }
  };

  const subtotal = products.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);

  const dropdownContent = showDropdown && searchTerm.length >= 2 && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl max-h-64 overflow-y-auto"
      style={{
        ...fixedDropdownStyle(dropdownPosition),
        minWidth: "300px",
      }}
    >
      {isSearching ? (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">Searching...</div>
      ) : results.length > 0 ? (
        results.map((product) => {
          const stock = product.quantity_available || 0;
          const unitPrice = product.effective_sale_price || product.sale_price || product.mrp || 0;
          
          return (
            <button
              key={product._id}
              type="button"
              onClick={() => handleAdd(product)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-emerald-50 transition-colors border-b border-slate-50 last:border-0"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium text-slate-800 truncate">{product.name}</div>
                <div className="text-[10px] text-slate-400">
                  SKU: {product.sku_number || "N/A"} | Unit: {product.unit || "N/A"} | ₹{unitPrice.toFixed(0)}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                <span className={`text-[10px] font-medium ${stock > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  Stock: {stock}
                </span>
                <span className="text-[11px] font-semibold text-emerald-600">Add</span>
              </div>
            </button>
          );
        })
      ) : (
        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">No products found</div>
      )}
    </div>
  );

  return (
    <div>
      <FieldLabel>Extra Products</FieldLabel>
      <div className="relative" ref={containerRef}>
        <div className="relative">
          <SearchIcon className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value.length >= 2) {
                updateDropdownPosition();
                setShowDropdown(true);
              } else {
                setShowDropdown(false);
              }
            }}
            onFocus={() => {
              if (searchTerm.length >= 2 && results.length > 0) {
                updateDropdownPosition();
                setShowDropdown(true);
              }
            }}
            placeholder="Search products to add..."
            className="w-full pl-8 pr-3 py-2 text-[12px] border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-transparent"
          />
        </div>
        {ReactDOM.createPortal(dropdownContent, document.body)}
      </div>

      {products.length > 0 ? (
        <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Product</th>
                {/* <th className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Usage (Qty &amp; Unit)</th> */}
                <th className="px-3 py-2 text-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">Quantity Used</th>
                <th className="px-3 py-2 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Unit</th>
                <th className="px-3 py-2 text-right text-[10px] font-bold text-slate-500 uppercase tracking-wider">Subtotal</th>
                <th className="px-3 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {products.map((product) => {
                {/* const usageQty = product.usage_qty === "" ? "" : Number(product.usage_qty ?? 1); */}
                const qtyUsed = product.quantity || 1;
                const rowSubtotal = (product.price || 0) * qtyUsed;
                return (
                  <tr key={product._id}>
                    <td className="px-3 py-2">
                      <span className="text-[12px] font-medium text-slate-800 block">{product.name}</span>
                      {product.sku_number && (
                        <span className="text-[10px] text-slate-400">SKU: {product.sku_number}</span>
                      )}
                    </td>
                    {/* <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <input
                          type="number"
                          min="0.1"
                          step="0.1"
                          value={usageQty}
                          onChange={(e) => handleUsageQtyInput(product._id, e.target.value)}
                          className="w-14 text-center border border-slate-200 rounded-md py-1 px-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        />
                        <span className="text-slate-400 text-[11px]">×</span>
                        <select
                          value={product.usage_unit || ""}
                          onChange={(e) => handleField(product._id, "usage_unit", e.target.value)}
                          className="border border-slate-200 rounded-md py-1 px-1 text-[11px] bg-white w-16 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        >
                          <option value="">Unit</option>
                          {USAGE_UNIT_OPTIONS.map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </div>
                    </td> */}
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleQuantity(product._id, -1)}
                          disabled={qtyUsed <= 1}
                          className="h-6 w-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <MinusIcon className="h-3 w-3" />
                        </button>
                        <span className="text-[12px] w-6 text-center">{qtyUsed}</span>
                        <button
                          type="button"
                          onClick={() => handleQuantity(product._id, 1)}
                          className="h-6 w-6 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                        >
                          <PlusIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-left">
                      <span className="text-[12px] text-slate-600">{product.unit || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-[12px] font-bold text-emerald-600">
                      ₹{rowSubtotal.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button 
                        type="button" 
                        onClick={() => handleRemove(product._id)} 
                        className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        aria-label="Remove product"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* <tfoot className="bg-slate-50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-[11px] font-bold text-slate-500 uppercase">
                  Extra Products Subtotal
                </td>
                <td className="px-3 py-2 text-right text-[13px] font-bold text-emerald-600">
                  ₹{subtotal.toFixed(2)}
                </td>
                <td />
              </tr>
            </tfoot> */}
          </table>
        </div>
      ) : (
        <p className="mt-3 text-[11px] text-slate-400">No extra products added.</p>
      )}
    </div>
  );
};

// ===================== MAIN TREATMENT PLAN TAB =====================

const TreatmentPlanTab = forwardRef(({ 
  onDirtyChange, 
  onSummaryChange,
  initialData
}, ref) => {
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isSavePlanModalOpen, setIsSavePlanModalOpen] = useState(false);
  const [isFromSavedPlan, setIsFromSavedPlan] = useState(false);

  const [proceduresList, setProceduresList] = useState([]);
  const [planProducts, setPlanProducts] = useState([]);
  const [appliedTreatmentPlan, setAppliedTreatmentPlan] = useState(null);
  const [diagnosticsOrdered, setDiagnosticsOrdered] = useState("");
  const [recommendations, setRecommendations] = useState({ quickRecommendations: [], notes: "" });
  const [isInitialized, setIsInitialized] = useState(false);

  const markDirty = () => onDirtyChange?.(true);
  const hasHydratedOnceRef = useRef(false);

  useEffect(() => {
    if (!initialData) {
      hasHydratedOnceRef.current = true;
      setIsInitialized(true);
      return;
    }

    if (proceduresList.length > 0 || planProducts.length > 0) {
      hasHydratedOnceRef.current = true;
      setIsInitialized(true);
      return;
    }

    if (hasHydratedOnceRef.current) {
      const incomingProcedures = initialData.proceduresList || [];
      const incomingProducts = initialData.planProducts || [];
      const localIsEmpty = proceduresList.length === 0 && planProducts.length === 0;
      const serverHasData = incomingProcedures.length > 0 || incomingProducts.length > 0;
      if (!(localIsEmpty && serverHasData)) {
        hasHydratedOnceRef.current = true;
        return;
      }
    }

    if (initialData.proceduresList && initialData.proceduresList.length > 0) {
      const loadedProcedures = initialData.proceduresList.map((proc) => {
        const procedureId = proc.procedure_id || proc._id;
        const procedureName = proc.name || proc.procedure_name || "Unknown Procedure";
        const price = parseFloat(proc.price || proc.override_price?.low || 0);
        const occurrences = parseInt(proc.occurrences || proc.quantity || 1);
        
        return {
          row_id: makeRowId(procedureId || Date.now()),
          procedure_id: procedureId,
          procedure_name: procedureName,
          price: price,
          occurrences: occurrences,
          products: [],
          productsLoading: false,
        };
      });
      setProceduresList(loadedProcedures);
    } else {
      setProceduresList([]);
    }

    if (initialData.planProducts && initialData.planProducts.length > 0) {
      const loadedProducts = initialData.planProducts.map((prod) => {
        const productId = prod.product_id || prod._id;
        return {
          _id: productId,
          name: prod.name || "Unknown Product",
          sku_number: prod.sku_number || "",
          unit: prod.unit || "",
          price: parseFloat(prod.price || 0),
          quantity: parseInt(prod.quantity || 1),
          usage_qty: prod.usage_qty || "",
          usage_unit: prod.usage_unit || "",
          source: prod.source || "treatment_plan",
          isFromPlan: prod.isFromPlan !== undefined ? prod.isFromPlan : true,
        };
      });
      setPlanProducts(loadedProducts);
    } else {
      setPlanProducts([]);
    }

    if (initialData.appliedTreatmentPlan) {
      setAppliedTreatmentPlan(initialData.appliedTreatmentPlan);
      setIsFromSavedPlan(true);
    } else {
      setAppliedTreatmentPlan(null);
      setIsFromSavedPlan(false);
    }

    if (initialData.diagnosticsOrdered) {
      setDiagnosticsOrdered(initialData.diagnosticsOrdered);
    }
    if (initialData.recommendations) {
      setRecommendations(initialData.recommendations);
    }

    hasHydratedOnceRef.current = true;
    setIsInitialized(true);
  }, [initialData, proceduresList.length, planProducts.length]);

  const proceduresTotal = proceduresList.reduce(
    (sum, p) => sum + (p.price || 0) * (p.occurrences || 1),
    0
  );
  const productsTotal = planProducts.reduce((sum, p) => sum + (p.price || 0) * (p.quantity || 1), 0);
  const itemCount = proceduresList.length + planProducts.length;
  const total = proceduresTotal + productsTotal;

  const hasTreatmentData = diagnosticsOrdered ||
    proceduresList.length > 0 ||
    planProducts.length > 0 ||
    (recommendations.quickRecommendations?.length > 0);

  const hasManualAdditions = itemCount > 0 && !isFromSavedPlan;

  useEffect(() => {
    onSummaryChange?.({ itemCount, total });
  }, [itemCount, total, onSummaryChange]);

  useImperativeHandle(ref, () => ({
    getData: () => {
      return {
        diagnosticsOrdered,
        proceduresList: proceduresList.map(p => ({
          procedure_id: p.procedure_id,
          name: p.procedure_name,
          price: p.price,
          quantity: p.occurrences,
          total: p.price * p.occurrences,
        })),
        planProducts: planProducts.map(p => ({
          product_id: p._id,
          name: p.name,
          sku_number: p.sku_number || "",
          unit: p.unit || "",
          price: p.price,
          quantity: p.quantity || 1,
          usage_qty: p.usage_qty || "",
          usage_unit: p.usage_unit || "",
          total: (p.price || 0) * (p.quantity || 1),
          source: p.source || "treatment_plan",
          isFromPlan: p.isFromPlan !== undefined ? p.isFromPlan : true,
        })),
        appliedTreatmentPlan,
        recommendations,
      };
    },
    clearAll: () => clearAllTreatmentData(),
    resetHydration: () => {
      hasHydratedOnceRef.current = false;
    },
    hasHydrated: () => {
      return hasHydratedOnceRef.current;
    },
    hasData: () => proceduresList.length > 0 || planProducts.length > 0,
  }));

  const handleApplyTreatmentPlan = ({ planMeta, procedures, products }) => {
    setAppliedTreatmentPlan(planMeta);
    setIsFromSavedPlan(true);

    if (procedures.length > 0) {
      const newRows = procedures
        .filter((p) => !proceduresList.some((existing) => existing.procedure_id === p.procedure_id))
        .map((p) => ({
          row_id: makeRowId(p.procedure_id),
          procedure_id: p.procedure_id,
          procedure_name: p.procedure_name,
          price: p.price,
          occurrences: p.occurrences,
          products: mapProcedureProducts(p.procDoc),
          productsLoading: false,
        }));
      setProceduresList([...proceduresList, ...newRows]);
    }

    if (products.length > 0) {
      const newProducts = products.filter(
        (p) => !planProducts.some((existing) => existing._id === p._id)
      );
      setPlanProducts([...planProducts, ...newProducts.map(({ key, ...rest }) => ({
        ...rest,
        source: "treatment_plan",
        isFromPlan: true,
      }))]);
    }
    markDirty();
    toast.success(`Applied "${planMeta.name}" — you can still adjust amounts before saving`);
  };

  const clearAllTreatmentData = () => {
    setProceduresList([]);
    setPlanProducts([]);
    setAppliedTreatmentPlan(null);
    setDiagnosticsOrdered("");
    setRecommendations({ quickRecommendations: [], notes: "" });
    setIsFromSavedPlan(false);
    markDirty();
  };

  const clearAppliedPlan = () => {
    setAppliedTreatmentPlan(null);
    setIsFromSavedPlan(false);
    markDirty();
  };

  const handleSaveAsTreatmentPlan = async (savedPlan) => {
    setIsFromSavedPlan(false);
    if (savedPlan) {
      setAppliedTreatmentPlan({ _id: savedPlan._id, name: savedPlan.name });
      setIsFromSavedPlan(true);
    }
    toast.success("Treatment plan saved! You can use it for future consultations.");
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="flex items-center gap-2 text-slate-400 text-[12px]">
          <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          Loading treatment plan...
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-end gap-2 flex-wrap mb-3">
        {hasManualAdditions && itemCount > 0 && (
          <button
            type="button"
            onClick={() => setIsSavePlanModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors shrink-0"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Save as Plan
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsPlanModalOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors shrink-0"
        >
          Select Plan
        </button>
        {appliedTreatmentPlan && (
          <span className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-lg shrink-0">
            Using: {appliedTreatmentPlan.name}
            <button type="button" onClick={clearAppliedPlan} className="hover:text-emerald-900">
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        )}
      </div>

      {hasManualAdditions && itemCount > 0 && (
        <div className="mb-3 text-[11px] text-blue-600 bg-blue-50 p-2 rounded-lg border border-blue-100 flex items-center gap-2">
          <span>💡</span>
          <span>
            You've added custom services/products. Click <strong>"Save as Plan"</strong> to save them as a reusable treatment plan.
          </span>
        </div>
      )}

      <div className="space-y-5">
        <ProceduresSection
          procedures={proceduresList}
          onChange={(val) => { 
            setProceduresList(val); 
            markDirty(); 
          }}
        />
        <ExtraProductsSection
          products={planProducts}
          onChange={(val) => { 
            setPlanProducts(val); 
            markDirty(); 
          }}
        />

        {itemCount > 0 && (
          <div className="flex justify-end items-center gap-3 pt-2 border-t border-slate-200">
            <span className="text-[11px] font-bold text-slate-500 uppercase">Treatment Total:</span>
            <span className="text-[14px] font-bold text-emerald-700">₹{total.toFixed(2)}</span>
          </div>
        )}
      </div>

      <TreatmentPlanPickerModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        onApply={handleApplyTreatmentPlan}
      />

      <SaveTreatmentPlanModal
        isOpen={isSavePlanModalOpen}
        onClose={() => setIsSavePlanModalOpen(false)}
        onSave={handleSaveAsTreatmentPlan}
        procedures={proceduresList}
        products={planProducts}
      />
    </div>
  );
});

TreatmentPlanTab.displayName = "TreatmentPlanTab";

export default TreatmentPlanTab;